import React from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { VettedProBadge } from '@fiverr-private/badges';
import { Text } from '@fiverr-private/typography';
import { IconButton } from '@fiverr-private/button';
import { ArrowRightIcon } from '@fiverr-private/visuals';
import { Image } from '@fiverr-private/media';
import { ElementType } from '@fiverr-private/bianka_js';
import { sendBiankaClick, sendMixpanelEvent } from '../../shared/biEvents';
import { EVENT_NAMES } from '../../shared/biEvents/eventNames';
import { enrichUrlWithParams } from '../../shared/urlEnrichment';
import { PRO_SECTION_TEXT_AND_CTA_URLS } from '../ProSection/constants';
import * as styles from './styles.ve.css';

interface ProSectionItemBadgeProps {
  domain: string;
  domainDesktop: string;
  ctaUrl: string;
  name: string;
  avatarSrc?: string;
  position?: number;
  isV16?: boolean;
  withBadge?: boolean;
}

const ProSectionItemBadge = ({
  domain,
  domainDesktop,
  ctaUrl,
  name,
  avatarSrc,
  position = 0,
  isV16 = false,
  withBadge = true,
}: ProSectionItemBadgeProps) => {
  const descriptionHeading = PRO_SECTION_TEXT_AND_CTA_URLS[position]?.descriptionHeading || domain;
  const enrichedCtaUrl = enrichUrlWithParams(ctaUrl, 'hplo_expert_card', position + 1);

  const handleClick = () => {
    sendBiankaClick({
      eventName: EVENT_NAMES.BIANKA.CLICKED_ON_EXPERTS_CAROUSEL,
      elementName: 'experts_carousel',
      elementType: ElementType.BUTTON,
      elementText: descriptionHeading,
      position: position + 1,
      isV16,
    });

    sendMixpanelEvent({
      eventName: EVENT_NAMES.MIXPANEL.LOHP_CLICKED_VETTED_EXPERT_CARD,
      clickedElement: domain,
      clickedElementOrder: position + 1,
      isV16,
    });
  };

  return (
    <Stack
      width={{ default: '100%', lg: 'max-content' }}
      height={{ default: '112px', sm: '160px', lg: '52px', xl: '68px' }}
      position="absolute"
      bottom="0px"
      left="0px"
      borderRadius="2xl"
      backgroundColor="white_70"
      paddingLeft={{ default: '4', sm: '6', lg: '1.5', xl: '2' }}
      paddingRight={{ default: '4', sm: '6', lg: '4', xl: '6' }}
      paddingBottom={{ default: '4', sm: '6', lg: '1.5', xl: '2' }}
      paddingTop={{ default: '0', lg: '1.5', xl: '2' }}
      className={styles.proSectionItemBadge}
    >
      <Stack
        gap={{ default: '4', sm: '6' }}
        direction="column"
        width="100%"
        height="100%"
        hidden={{ default: false, lg: true }}
        className={styles.proSectionItemBadgeContent}
        justifyContent="flexEnd"
      >
        {withBadge && <VettedProBadge />}
        <Stack gap={{ default: '5', sm: '12' }} justifyContent="spaceBetween" alignItems="flexEnd">
          <Text size={{ default: 'b_xl', sm: 'b_xxl' }} className={styles.proSectionItemDomain} color="bodyPrimary">
            {domain}
          </Text>
          <IconButton
            aria-label="Hire Now"
            href={enrichedCtaUrl}
            intent="primary"
            shape="circle"
            target="_blank"
            variant="filled"
            size="md"
            flexShrink="0"
            className={styles.proSectionItemBadgeButton}
            hidden={{ default: false, sm: true }}
            onClick={handleClick}
          >
            <ArrowRightIcon color="white" size="md" />
          </IconButton>
          <IconButton
            aria-label="Hire Now"
            href={enrichedCtaUrl}
            intent="primary"
            shape="circle"
            target="_blank"
            variant="filled"
            size="lg"
            flexShrink="0"
            className={styles.proSectionItemBadgeButton}
            hidden={{ default: true, sm: false }}
            onClick={handleClick}
          >
            <ArrowRightIcon color="white" size="lg" />
          </IconButton>
        </Stack>
      </Stack>
      <Stack
        gap={{ lg: '1.5', xl: '2' }}
        width="100%"
        height="100%"
        hidden={{ default: true, lg: false }}
        className={styles.proSectionItemBadgeContent}
      >
        <Container
          width={{ lg: '40px', xl: '52px' }}
          height={{ lg: '40px', xl: '52px' }}
          borderRadius="circle"
          backgroundColor="grey_400"
          alignItems="center"
          justifyContent="center"
          flexShrink="0"
          overflow="hidden"
          className={styles.proSectionItemBadgeAvatarContainer}
        >
          {!!avatarSrc && <Image src={avatarSrc} alt="Avatar" />}
        </Container>
        <Stack
          direction="column"
          gap={{ lg: '0.5', xl: '1' }}
          className={styles.proSectionItemBadgeDetailsContainer}
          justifyContent="center"
        >
          <Stack gap={{ lg: '1', xl: '1.5' }} alignItems="center" className={styles.proSectionItemBadgeNameContainer}>
            <Text
              size={{ lg: 'b_xs', xl: 'b_sm' }}
              fontWeight="bold"
              color="bodyPrimary"
              className={styles.proSectionItemBadgeName}
              width="max-content"
            >
              {name}
            </Text>
            {withBadge && <VettedProBadge variant="short" />}
          </Stack>

          <Text size={{ default: 'b_xl', sm: 'b_xxl' }} className={styles.proSectionItemDomain} color="bodyPrimary">
            {domainDesktop}
          </Text>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ProSectionItemBadge;
